import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { type ContactBankAccountSchema, contactBankAccountSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { ContactBankAccountForm } from "@/forms/contact-bank-account-form";
import { client } from "@/hono";

/** Props Interface */
interface AddContactBankAccountModalProps extends ModalProps {
	contactId: string;
}

export function AddContactBankAccountModal({
	isOpen,
	onClose,
	contactId,
}: AddContactBankAccountModalProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const qc = useQueryClient();
	const methods = useForm<ContactBankAccountSchema>({
		defaultValues: { iban: "", bic: null, accountHolder: null, bankName: null, isDefault: false },
		resolver: resolver(contactBankAccountSchema),
	});

	/** Functions */
	async function handleOnSubmit(json: ContactBankAccountSchema) {
		const response = await client.contacts[":contactId"]["bank-accounts"].$post({
			param: { contactId },
			json,
		});
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["contact", "incoming-invoices"]) });
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("contacts.bank_accounts.add")}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading}
			>
				<Modal.Body>
					<ContactBankAccountForm />
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
